import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import DashboardListViewData from './dashboardListViewData';
import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeList } from "react-window";
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
  name: 250,
  cost_status: 250,
  total_expenses: 250,
  status: 250,
  type: 250,
  region: 250,
  site: 250,
  pole: 250,
  metier: 250,
  purchase_price: 250,
};

const machine_status = [
  { id: 'Valide', name: 'Valide' },
  { id: 'AvecReparation', name: 'Avec Reparation' },
  { id: 'NonConforme', name: 'Non Conforme' },
];

const ListView = React.memo(({ showResponsiveFilter, scrollRef, machines, regions, sites, poles, metiers, types, status, filters, setFilters, sortBy, setSortBy, setOffset, filteredMachinesCount, setMachinesFetched }) => {

  const { t } = useTranslation();
  const authenticatedUser = useSelector((state) => state.AuthenticatedUser);

  const [machineColumnWidth, setMachineColumnWidth] = useState(() => {
    const storedWidths = JSON.parse(localStorage.getItem('dashboardColumnWidth')) || {};
    return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
  });

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    localStorage.setItem('dashboardColumnWidth', JSON.stringify(machineColumnWidth));
  }, [machineColumnWidth]);


  const handleColumnWidthChange = (value, columnKey) => {
    setMachineColumnWidth({ ...machineColumnWidth, [columnKey]: value })
  }

  // Update window height on resize
  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowHeight(window.innerHeight);
    }, 300); // 300ms debounce delay

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel(); // Cancel any pending debounced calls
    };
  }, []);

  const tableHeadRef = useRef(null);

  // synchronize horizontal scroll between table head and list
  useSyncHorizontalScroll(tableHeadRef, scrollRef);

  // Calculate fetched items on mount
  useCalculateFetchedItems(filteredMachinesCount, setMachinesFetched, 45, showResponsiveFilter ? 650 : 550);

  // Handle scroll and fetch more items
  const handleScrollList = useScrollFetchData(setOffset, setMachinesFetched, filteredMachinesCount, 45, showResponsiveFilter ? 650 : 550);

  const handleChangeStatus = (status) => {
    setFilters((prev) => ({ ...prev, selectedStatus: status }));
  }

  const handleChangeType = (type) => {
    setFilters((prev) => ({ ...prev, selectedType: type }));
  }

  const handleChangeRegion = (region) => {
    setFilters((prev) => ({ ...prev, selectedRegion: region, selectedSite: '', selectedPole: '', selectedMetier: '', selectedStatus: '', selectedType: '', selectedMark: '' }));
  }

  const handleChangeSite = (site) => {
    setFilters((prev) => ({ ...prev, selectedSite: site, selectedPole: '', selectedMetier: '', selectedStatus: '', selectedType: '', selectedMark: '' }));
  }

  const handleChangePole = (pole) => {
    setFilters((prev) => ({ ...prev, selectedPole: pole, selectedMetier: '', selectedStatus: '', selectedType: '', selectedMark: '' }));
  }

  const handleChangeMetier = (metier) => {
    setFilters((prev) => ({ ...prev, selectedMetier: metier, selectedStatus: '', selectedType: '', selectedMark: '' }));
  }

  const handleSort = (field) => {
    setSortBy((prevSortBy) => {
      const nextSortOrder = prevSortBy[field] === null ? 'asc'
        : prevSortBy[field] === 'asc' ? 'desc'
          : null;

      return Object.fromEntries(
        Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
      );
    });

  };

  const Row = ({ index, style, data }) => {
    const { machines, regions } = data;
    const machine = index === 0 ? machines[0] : machines[index];
    return (
      <Box key={index} style={{ ...style, width: 'fit-content' }}>
        <DashboardListViewData
          machine={machine}
          regions={regions}
          filters={filters}
          columnWidth={machineColumnWidth}
        />
      </Box>
    );
  };

  return (
    <Box className='table'>
      <Box className='tableHead' ref={tableHeadRef}>
        <CustomHeaderCell
          label={t('machineName')}
          columnKey='name'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={false}
          isFixed={true}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        {regions.length > 1 && (
          <CustomHeaderCell
            label={t('Regions')}
            columnKey='region'
            sortBy={sortBy}
            handleSort={handleSort}
            sortable={true}
            isFilter={regions.length > 1}
            value={filters.selectedRegion}
            onChange={(e) => handleChangeRegion(e.target.value)}
            options={regions}
            columnWidth={machineColumnWidth}
            onColumnWidthChange={handleColumnWidthChange}
          />
        )}
        <CustomHeaderCell
          label={t('sites')}
          columnKey='site'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={authenticatedUser.user_site_ids?.length > 1}
          value={filters.selectedSite}
          onChange={(e) => handleChangeSite(e.target.value)}
          options={sites}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('poles')}
          columnKey='pole'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={authenticatedUser.user_pole_ids?.length > 1}
          value={filters.selectedPole}
          onChange={(e) => handleChangePole(e.target.value)}
          options={poles}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Métier')}
          columnKey='metier'
          sortable={false}
          isFilter={authenticatedUser.user_metier_ids?.length > 1}
          value={filters.selectedMetier}
          onChange={(e) => handleChangeMetier(e.target.value)}
          options={metiers}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Statut de contrôle')}
          columnKey='status'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={true}
          value={filters.selectedStatus}
          onChange={(e) => handleChangeStatus(e.target.value)}
          options={[...machine_status.filter((ms) => status.includes(ms.id)), { id: 'Indéfini', name: 'Indéfini' }]}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Type')}
          columnKey='type'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={true}
          value={filters.selectedType}
          onChange={(e) => handleChangeType(e.target.value)}
          options={types}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label="Prix d'achat"
          columnKey='purchase_price'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={false}
          isFixed={true}
          fixSide='right'
          rightLabel={true}
          fixSidePosition={machineColumnWidth.total_expenses}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label='Dépense'
          columnKey='total_expenses'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={false}
          isFixed={true}
          fixSide='right'
          rightLabel={true}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
      </Box>

      <Box className='tableBody'>
        <FixedSizeList
          ref={scrollRef}
          height={Math.min(
            filteredMachinesCount * 45 + 15,
            showResponsiveFilter ? windowHeight - 650 : windowHeight - 550
          )}
          width="100%"
          itemCount={filteredMachinesCount}
          itemSize={45}
          itemData={{ machines, regions }}
          onScroll={handleScrollList}
        >
          {Row}
        </FixedSizeList>
      </Box>
    </Box>

  );
});

export default ListView;